import React, { useState } from 'react';
import { Phone, MapPin, Mail, Clock, Send } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_BASE_URL = 'https://rudrakshenterprises.liveindiatech.com/rudraksh_backend';

export default function ContactPage({ cartCount, setIsCartOpen, setActiveCategory, setCurrentPage, setSearchQuery }) {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        message: ''
    });

    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/contacts/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    message: formData.message
                })
            });

            if (response.ok) {
                alert('Thank you! Your message has been sent successfully and saved to admin panel.');
                setFormData({ name: '', phone: '', email: '', message: '' });
            } else {
                alert('Failed to send message. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting contact form:', error);
            alert('Server error. Make sure Django backend is running.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-white">
            {/* Navbar */}
            <Navbar
                cartCount={cartCount}
                setIsCartOpen={setIsCartOpen}
                setActiveCategory={setActiveCategory}
                setCurrentPage={setCurrentPage}
                setSearchQuery={setSearchQuery}
            />

            {/* Main Contact Section */}
            <main className="flex-1 w-full px-4 sm:px-8 md:px-12 lg:px-20 py-16 bg-gray-50/50">
                <div className="text-center max-w-2xl mx-auto mb-16 space-y-2">
                    <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">Contact Us</h2>
                    <p className="text-xs sm:text-sm text-gray-600">
                        We are here to help! Reach out to us via phone, email, or by filling out the form below. Our team will get back to you as soon as possible.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">

                    {/* Left Side: Contact Information */}
                    <div className="space-y-6">
                        <h3 className="text-2xl font-black text-orange-600">Contact Information</h3>

                        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-start gap-4">
                            <div className="p-3 bg-orange-50 text-orange-600 rounded-2xl">
                                <Phone size={22} />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-base">Telephone</h4>
                                <p className="text-sm text-orange-600 font-semibold mt-1">+91 98919 90313, +91 99119 92162</p>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-start gap-4">
                            <div className="p-3 bg-orange-50 text-orange-600 rounded-2xl">
                                <MapPin size={22} />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-base">Head Office</h4>
                                <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">
                                    L-325, Sec-23, Sanjay Nagar, Ghaziabad - 201002
                                </p>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-start gap-4">
                            <div className="p-3 bg-orange-50 text-orange-600 rounded-2xl">
                                <Mail size={22} />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-base">Email</h4>
                                <p className="text-sm text-orange-600 font-semibold mt-1">support@rudrakshenterprises.com</p>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-start gap-4">
                            <div className="p-3 bg-orange-50 text-orange-600 rounded-2xl">
                                <Clock size={22} />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-base">Order Time</h4>
                                <p className="text-xs sm:text-sm text-gray-500 mt-1">We are open 24*7</p>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Contact Form */}
                    <div className="bg-white p-8 sm:p-10 rounded-3xl shadow-sm border border-gray-100">
                        <h3 className="text-2xl font-black text-orange-600 text-center mb-2">Contact Form</h3>
                        <p className="text-xs text-gray-500 text-center mb-8">
                            For all enquiries, please email us using the contact form below.
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1.5">Your Name</label>
                                <input
                                    type="text"
                                    placeholder="Full Name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:ring-2 focus:ring-orange-500 outline-none text-gray-800 bg-gray-50/30"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1.5">Your Number</label>
                                <input
                                    type="text"
                                    placeholder="Phone Number"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:ring-2 focus:ring-orange-500 outline-none text-gray-800 bg-gray-50/30"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1.5">E-Mail Address</label>
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:ring-2 focus:ring-orange-500 outline-none text-gray-800 bg-gray-50/30"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1.5">Message</label>
                                <textarea
                                    placeholder="Message"
                                    rows="4"
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-xs sm:text-sm focus:ring-2 focus:ring-orange-500 outline-none text-gray-800 bg-gray-50/30 resize-none"
                                    required
                                ></textarea>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-xl text-xs sm:text-sm transition shadow-md flex items-center justify-center gap-2 cursor-pointer border-none"
                            >
                                {loading ? 'Sending...' : 'Submit'} <Send size={16} />
                            </button>
                        </form>
                    </div>

                </div>
            </main>

            {/* Footer */}
            <Footer
                setActiveCategory={setActiveCategory}
                setCurrentPage={setCurrentPage}
            />
        </div>
    );
}